import React, { useEffect, useState } from 'react';
import { DiagnosticAssessmentResult } from './types';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { usePreKAudio } from '../../hooks/usePreKAudio';
import { ACCURACY_PASS_THRESHOLD } from '../../types/constants';
import JourneyPath from '../PreKPractice/shared/JourneyPath';

interface PreKResultScreenProps {
  results: DiagnosticAssessmentResult;
  assessmentType: 'numbernaming' | 'subitizing' | 'counting' | 'whatcomesnext';
  onContinue: () => void;
}

// Shared action button component
const ActionButton: React.FC<{
  text: string;
  onClick: () => void;
  className?: string;
}> = ({ text, onClick, className = "" }) => (
  <motion.button
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: 0.4 }}
    whileHover={{ scale: 1.08, rotate: 1 }}
    whileTap={{ scale: 0.92 }}
    onClick={onClick}
    className={`h-20 lg:h-24 px-16 rounded-3xl font-bold text-2xl lg:text-3xl shadow-xl transition-all duration-300 cursor-pointer hover:shadow-2xl transform-gpu ${className}`}
  >
    {text}
  </motion.button>
);

// Confetti particle component
const ConfettiParticle: React.FC<{ delay: number }> = ({ delay }) => {
  const colors = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'];
  const color = colors[Math.floor(Math.random() * colors.length)];

  return (
    <motion.div
      className="absolute w-2 h-2 rounded-full"
      style={{ backgroundColor: color }}
      initial={{
        x: Math.random() * window.innerWidth,
        y: -20,
        rotate: 0,
        opacity: 1
      }}
      animate={{
        y: window.innerHeight + 100,
        rotate: 360,
        opacity: 0
      }}
      transition={{
        duration: 2 + Math.random() * 1,
        delay: delay,
        ease: "easeOut"
      }}
    />
  );
};

// Helper to map assessment type to track ID for JourneyPath
const assessmentTypeToTrackId = (assessmentType: string): string => {
  switch (assessmentType) {
    case 'subitizing': return 'TRACK_PREK_SUBITIZING';
    case 'counting': return 'TRACK_PREK_COUNTING';
    case 'whatcomesnext': return 'TRACK_PREK_WHATCOMESNEXT';
    default: return 'TRACK_PREK_NAMING';
  }
};

const PreKResultScreen: React.FC<PreKResultScreenProps> = ({ results, assessmentType, onContinue }) => {
  const navigate = useNavigate();
  const { playAudio } = usePreKAudio();
  const [showConfetti, setShowConfetti] = useState(false);

  const correctAnswers = results.correctAnswers || 0;
  const totalQuestions = results.totalQuestions || 0;

  const accuracy = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;

  // PreK pass is accuracy-only (no CQPM requirement)
  const passed = accuracy >= ACCURACY_PASS_THRESHOLD;

  // Get assessment-specific display name
  const assessmentDisplayName = assessmentType === 'numbernaming' ? 'Number Naming' :
                                assessmentType === 'subitizing' ? 'Subitizing' :
                                assessmentType === 'counting' ? 'Counting' : 'What Comes Next';

  // Map assessment type to audio filename format
  const getAudioFilename = (prefix: string, assessmentType: string) => {
    const audioType = assessmentType === 'numbernaming' ? 'number_naming' : assessmentType;
    return `results/${prefix}_${audioType}.mp3`;
  };

  // Play audio and show confetti when pass screen loads, or play near miss audio
  useEffect(() => {
    if (passed) {
      // Play pass audio
      playAudio({ filename: getAudioFilename('result_pass', assessmentType) }).catch(() => {});

      // Show confetti for 600ms
      setShowConfetti(true);
      const confettiTimer = setTimeout(() => {
        setShowConfetti(false);
      }, 600);

      return () => clearTimeout(confettiTimer);
    } else {
      // Play failed audio
      playAudio({ filename: getAudioFilename('result_fail', assessmentType) }).catch(() => {});
    }
  }, [passed, playAudio, assessmentType]);

  // Get track mapping for each assessment type
  const getTrackMapping = (type: string) => {
    switch (type) {
      case 'numbernaming': return {
        trackId: 'TRACK_PREK_NAMING',
        practiceUrl: '/pre-k-practice?track=naming',
        nextAssessment: 'subitizing'
      };
      case 'subitizing': return {
        trackId: 'TRACK_PREK_SUBITIZING',
        practiceUrl: '/pre-k-practice?track=subitizing',
        nextAssessment: 'counting'
      };
      case 'counting': return {
        trackId: 'TRACK_PREK_COUNTING',
        practiceUrl: '/pre-k-practice?track=counting',
        nextAssessment: 'whatcomesnext'
      };
      case 'whatcomesnext': return {
        trackId: 'TRACK_PREK_WHATCOMESNEXT',
        practiceUrl: '/pre-k-practice?track=whatcomesnext',
        nextAssessment: null // Last assessment
      };
      default: return {
        trackId: 'TRACK_PREK_NAMING',
        practiceUrl: '/pre-k-practice?track=naming',
        nextAssessment: 'subitizing'
      };
    }
  };

  const handleContinue = () => {
    const mapping = getTrackMapping(assessmentType);
    if (passed) {
      if (mapping.nextAssessment === null) {
        window.location.replace('/');
      } else {
        onContinue();
      }
    } else {
      navigate(mapping.practiceUrl);
    }
  };


  if (passed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col relative overflow-hidden">
        {/* Confetti Animation */}
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 50 }, (_, i) => (
              <ConfettiParticle key={i} delay={i * 0.02} />
            ))}
          </div>
        )}

        {/* Header Row - JourneyPath */}
        <div className="p-6">
          <JourneyPath currentTrack={assessmentTypeToTrackId(assessmentType)} />
        </div>

        {/* Main Content Area - Centered */}
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center space-y-8">
            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-5xl lg:text-6xl font-bold text-gray-800"
            >
              Great Job!
            </motion.h1>

            {/* Subtitle */}
            <motion.h2
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-3xl lg:text-4xl font-bold text-green-600 flex items-center justify-center gap-3"
            >
              <span className="text-4xl lg:text-5xl">✓</span>
              {assessmentDisplayName} Complete
            </motion.h2>

            {/* Metrics */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-lg lg:text-xl text-gray-600 font-medium space-y-2"
            >
              <div>Correct: {correctAnswers} of {totalQuestions} • Accuracy: {accuracy}%</div>
            </motion.div>

            {/* Continue Button */}
            <ActionButton
              text="Continue"
              onClick={handleContinue}
              className="bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700"
            />
          </div>
        </div>
      </div>
    );
  }

  // Failed case - consistent styling with pass
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      {/* Header Row - JourneyPath */}
      <div className="p-6">
        <JourneyPath currentTrack={assessmentTypeToTrackId(assessmentType)} />
      </div>

      {/* Main Content Area - Centered */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="text-center space-y-8">
          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl lg:text-6xl font-bold text-gray-800"
          >
            Nice try!
          </motion.h1>

          {/* Subtitle */}
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-3xl lg:text-4xl font-bold text-purple-600"
          >
            Let's practice it first!
          </motion.h2>

          {/* Metrics */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-lg lg:text-xl text-gray-600 font-medium space-y-2"
          >
            <div>Correct: {correctAnswers} of {totalQuestions} • Accuracy: {accuracy}% (need ≥{ACCURACY_PASS_THRESHOLD}%)</div>
          </motion.div>

          {/* Continue Button */}
          <ActionButton
            text="Continue"
            onClick={handleContinue}
            className="bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700"
          />
        </div>
      </div>
    </div>
  );
};

export default PreKResultScreen;